import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Breadcrumb, Button, IconButton, Label, LABEL_TYPE } from '@/components/generic';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { AuthContext } from '@/contexts';
import { VideoThemeContext } from '@/contexts/VideoThemeProvider';
import { PERMISSION_TYPE } from '@/enums';
import { PRIVATE_PAGE_ROUTES } from '@/enums/RouterEnums';
import { TABS } from '@/enums/ThemeTabsEnum';
import { EditPenIcon, PremiumIcon, TrashIcon, VideoThemeIcon } from '@/resources';

type HeaderProps = {
  currentTab: string;
  setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
  handlePublish: () => void;
};

export const Header = ({
  currentTab,
  setIsDeleteModalOpen,
  setCurrentTab,
  handlePublish
}: HeaderProps) => {
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const { themeData } = useContext(VideoThemeContext);
  const { permissions } = useContext(AuthContext);
  const deleteVideoThemeAccess = permissions.includes(PERMISSION_TYPE.DELETE_VIDEO_THEME);
  const editVideoThemeAccess = permissions.includes(PERMISSION_TYPE.UPDATE_VIDEO_THEME);

  return (
    <div className='w-full'>
      <div className='!md:w-full md:mx-20 md:mt-7'>
        <Breadcrumb
          icon={<VideoThemeIcon className='stroke-[0.5] text-lg text-primary-blue' />}
          iconPath={PRIVATE_PAGE_ROUTES.THEMES}
          path={[
            { label: themeData?.name ?? '', url: `${PRIVATE_PAGE_ROUTES.THEMES}/${themeData?.id}` },
            { label: t(currentTab) }
          ]}
        />
        <div className='mt-8 hidden flex-row items-center justify-between md:flex'>
          <div className='flex flex-row justify-center gap-x-6'>
            <Label text={themeData?.name} type={LABEL_TYPE.H1} />
            {themeData?.type === 1 && (
              <div className='flex items-center justify-center'>
                <PremiumIcon className='size-6' />
              </div>
            )}
          </div>
          <div className='flex flex-row items-center gap-4'>
            {editVideoThemeAccess && (
              <>
                <Button disabled={themeData?.isPublish} handleOnClick={handlePublish}>
                  {t('publish')}
                </Button>
                <IconButton
                  variant={IconButtonVariant.PRIMARY}
                  handleOnClick={() =>
                    navigate(`${PRIVATE_PAGE_ROUTES.THEMES}/${themeData?.id}/edit`)
                  }>
                  <EditPenIcon className='h-4 w-3 text-primary-blue md:h-5 md:w-4' />
                </IconButton>
              </>
            )}
            {deleteVideoThemeAccess && (
              <IconButton
                variant={IconButtonVariant.PRIMARY}
                handleOnClick={() => setIsDeleteModalOpen(true)}>
                <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />
              </IconButton>
            )}
          </div>
        </div>
        <div
          className={
            'no-scrollbar mt-4 flex flex-row overflow-x-scroll md:mt-8 lg:overflow-x-hidden'
          }>
          {Object.values(TABS).map((tab) => (
            <div
              key={tab}
              className={`cursor-pointer whitespace-nowrap rounded px-5 py-2.5 ${
                currentTab === tab && 'bg-secondary-pale-blue-gray font-bold'
              }`}
              onClick={() => {
                localStorage.setItem('currentTabTheme', tab);
                setCurrentTab(tab);
              }}>
              {t(tab)}
            </div>
          ))}
        </div>
        <div className='w-full border-b' />
      </div>
    </div>
  );
};
